import React from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useLanguage } from '../../../../contexts/LanguageContext';
import useOrderRequestDetail from '../hooks/useOrderRequestDetail';
import ApprovalHistoryTimeline from './ApprovalHistoryTimeline';
import ChassisSelectionDialog from './ChassisSelectionDialog';
import RejectReturnDialog from './RejectReturnDialog';

const OrderRequestDetailDialog = ({ visible, order, activeTabKey, onHide, onActionComplete, brands, paymentTermsList }) => {
    const { getCurrentLocale } = useLanguage();

    const {
        th,
        detail,
        detailLoading,
        brand,
        model,
        matnr,
        maktx,
        quantity,
        deliveryDate,
        paymentTerms,
        paymentDate,
        canEdit,
        canApprove,
        canReject,
        canReturn,
        approvalHistory,
        orderId,
        isDraft,
        isPending,
        isReadonly,
        isActionLoading,
        modelOptions,
        materialOptions,
        modelsLoading,
        materialsLoading,
        minDate,
        onBrandChange,
        onModelChange,
        onMaterialChange,
        setDeliveryDate,
        setPaymentTerms,
        setPaymentDate,
        handleQuantityChange,
        handleUpdateDraft,
        handleSubmitForApproval,
        handleApprove,
        handleReject,
        handleReturn,
        handleRejectReturnConfirm,
        handleChassisApprove,
        chassisDialogVisible,
        setChassisDialogVisible,
        rejectReturnDialogVisible,
        setRejectReturnDialogVisible,
        rejectReturnAction
    } = useOrderRequestDetail({ order, visible, activeTabKey, onActionComplete, brands, paymentTermsList });

    const formatDateDisplay = (date) => {
        if (!date) return '';
        try {
            const d = date instanceof Date ? date : new Date(date);
            return d.toLocaleDateString(getCurrentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch {
            return '';
        }
    };

    const renderFooter = () => {
        if (isDraft && canEdit) {
            return (
                <div className="flex justify-content-end gap-2">
                    <Button
                        label={th('vehicleSalesOrderRequests.buttons.cancel')}
                        icon="pi pi-times"
                        outlined
                        onClick={onHide}
                        disabled={isActionLoading}
                    />
                    <Button
                        label={th('vehicleSalesOrderRequests.buttons.save')}
                        icon="pi pi-save"
                        onClick={handleUpdateDraft}
                        loading={isActionLoading}
                    />
                    <Button
                        label={th('vehicleSalesOrderRequests.buttons.submitForApproval')}
                        icon="pi pi-check"
                        className="p-button-success"
                        onClick={handleSubmitForApproval}
                        loading={isActionLoading}
                    />
                </div>
            );
        }

        if (isPending) {
            return (
                <div className="flex justify-content-end gap-2">
                    <Button
                        label={th('vehicleSalesOrderRequests.buttons.close')}
                        icon="pi pi-times"
                        outlined
                        onClick={onHide}
                        disabled={isActionLoading}
                    />
                    {canReturn && (
                        <Button
                            label={th('vehicleSalesOrderRequests.buttons.return')}
                            icon="pi pi-replay"
                            className="p-button-warning"
                            onClick={handleReturn}
                            loading={isActionLoading}
                        />
                    )}
                    {canReject && (
                        <Button
                            label={th('vehicleSalesOrderRequests.buttons.reject')}
                            icon="pi pi-times-circle"
                            className="p-button-danger"
                            onClick={handleReject}
                            loading={isActionLoading}
                        />
                    )}
                    {canApprove && (
                        <Button
                            label={th('vehicleSalesOrderRequests.buttons.approve')}
                            icon="pi pi-check"
                            onClick={handleApprove}
                            loading={isActionLoading}
                        />
                    )}
                </div>
            );
        }

        return (
            <div className="flex justify-content-end gap-2">
                <Button
                    label={th('vehicleSalesOrderRequests.buttons.close')}
                    icon="pi pi-times"
                    outlined
                    onClick={onHide}
                />
            </div>
        );
    };

    return (
        <>
            <Dialog
                header={`${th('vehicleSalesOrderRequests.detail.title')} #${detail?.dmsOrderNumber || order?.dmsOrderNumber || ''}`}
                visible={visible}
                onHide={onHide}
                style={{ width: '70vw', maxWidth: '800px' }}
                footer={renderFooter()}
                modal
                maximizable
            >
                {detailLoading && !detail ? (
                    <div className="flex justify-content-center p-5">
                        <ProgressSpinner />
                    </div>
                ) : detail ? (
                    <div className="grid">
                        <div className="col-12">
                            <h5 className="mt-2">{th('vehicleSalesOrderRequests.detail.headerInfo')}</h5>
                        </div>

                        {detail.statusText && (
                            <div className="col-12">
                                <div className="field">
                                    <label>{th('vehicleSalesOrderRequests.columns.status')}</label>
                                    <InputText value={detail.statusText} readOnly className="w-full" />
                                </div>
                            </div>
                        )}

                        {/* Marka */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.columns.brand')}</label>
                                {isReadonly ? (
                                    <InputText value={brand || ''} readOnly className="w-full" />
                                ) : (
                                    <Dropdown
                                        value={brand}
                                        options={brands}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={onBrandChange}
                                        placeholder={th('vehicleSalesOrderRequests.columns.brand')}
                                        className="w-full"
                                        filter
                                        showClear
                                    />
                                )}
                            </div>
                        </div>

                        {/* Model */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.columns.model')}</label>
                                {isReadonly ? (
                                    <InputText value={model || ''} readOnly className="w-full" />
                                ) : (
                                    <Dropdown
                                        value={model}
                                        options={modelOptions}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={onModelChange}
                                        placeholder={th('vehicleSalesOrderRequests.columns.model')}
                                        className="w-full"
                                        filter
                                        showClear
                                        loading={modelsLoading}
                                        disabled={!brand}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Malzeme No */}
                        <div className="col-12">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.columns.matnr')}</label>
                                {isReadonly ? (
                                    <InputText
                                        value={materialOptions.find((m) => m.matnr === matnr)?.displayText || matnr || ''}
                                        readOnly
                                        className="w-full"
                                    />
                                ) : (
                                    <Dropdown
                                        value={matnr}
                                        options={materialOptions}
                                        optionLabel="displayText"
                                        optionValue="matnr"
                                        onChange={onMaterialChange}
                                        placeholder={th('vehicleSalesOrderRequests.columns.matnr')}
                                        className="w-full"
                                        filter
                                        showClear
                                        loading={materialsLoading}
                                        disabled={!brand || !model}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Miktar */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.columns.quantity')}</label>
                                {isReadonly ? (
                                    <InputText value={String(quantity ?? '')} readOnly className="w-full" />
                                ) : (
                                    <InputText
                                        value={String(quantity ?? '')}
                                        onChange={handleQuantityChange}
                                        className="w-full"
                                        keyfilter="pint"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Teslimat Tarihi */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.columns.deliveryDate')}</label>
                                {isReadonly ? (
                                    <InputText value={formatDateDisplay(deliveryDate)} readOnly className="w-full" />
                                ) : (
                                    <Calendar
                                        value={deliveryDate}
                                        onChange={(e) => setDeliveryDate(e.value)}
                                        minDate={minDate}
                                        dateFormat="dd.mm.yy"
                                        showIcon
                                        className="w-full"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Odeme Kosullari */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.detail.paymentTerms')}</label>
                                {isReadonly ? (
                                    <InputText
                                        value={paymentTermsList.find((p) => p.value === paymentTerms)?.label || paymentTerms || ''}
                                        readOnly
                                        className="w-full"
                                    />
                                ) : (
                                    <Dropdown
                                        value={paymentTerms}
                                        options={paymentTermsList}
                                        optionLabel="label"
                                        optionValue="value"
                                        onChange={(e) => setPaymentTerms(e.value)}
                                        placeholder={th('vehicleSalesOrderRequests.detail.paymentTerms')}
                                        className="w-full"
                                        filter
                                        showClear
                                    />
                                )}
                            </div>
                        </div>

                        {/* Odeme Tarihi */}
                        <div className="col-12 md:col-6">
                            <div className="field">
                                <label>{th('vehicleSalesOrderRequests.columns.paymentDate')}</label>
                                {isReadonly ? (
                                    <InputText value={formatDateDisplay(paymentDate)} readOnly className="w-full" />
                                ) : (
                                    <Calendar
                                        value={paymentDate}
                                        onChange={(e) => setPaymentDate(e.value)}
                                        minDate={minDate}
                                        dateFormat="dd.mm.yy"
                                        showIcon
                                        className="w-full"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Onay Gecmisi */}
                        {approvalHistory.length > 0 && (
                            <>
                                <div className="col-12">
                                    <h5 className="mt-2 mb-2">{th('vehicleSalesOrderRequests.detail.approvalHistory')}</h5>
                                </div>
                                <div className="col-12">
                                    <ApprovalHistoryTimeline history={approvalHistory} />
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </Dialog>

            <ChassisSelectionDialog
                visible={chassisDialogVisible}
                onHide={() => setChassisDialogVisible(false)}
                orderId={orderId}
                quantity={quantity}
                onConfirm={handleChassisApprove}
                isLoading={isActionLoading}
            />

            <RejectReturnDialog
                visible={rejectReturnDialogVisible}
                onHide={() => setRejectReturnDialogVisible(false)}
                action={rejectReturnAction}
                onConfirm={handleRejectReturnConfirm}
                isLoading={isActionLoading}
            />
        </>
    );
};

export default OrderRequestDetailDialog;
